import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Hand, Factory, Leaf, BadgeCheck } from "lucide-react";
import { IMAGES } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const PILLARS = [
    { icon: Hand, title: "Skilled Craftsmanship", text: "Saddle stitching, edge painting and hand finishing by trained artisans." },
    { icon: Factory, title: "Made-to-Order", text: "Modern machinery and flexible OEM/ODM runs built around your specification." },
    { icon: Leaf, title: "Responsible Sourcing", text: "Responsible manufacturing practices that minimize environmental impact." },
    { icon: BadgeCheck, title: "Continuous Improvement", text: "Tiered quality control at cutting, stitching, assembly and final inspection." },
];

export const CraftStory = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <section id="craft" ref={ref} data-testid="craft-section" className="relative py-24 sm:py-32 bg-[#181410] overflow-hidden grain">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                <div className="relative">
                    <motion.div style={{ y: imgY }} className="relative">
                        <div className="overflow-hidden rounded-md border border-[#2E2620] group">
                            <img
                                src="/assets/founder.png"
                                alt="Mohd Farrukh, Founder of FnB International"
                                loading="lazy"
                                className="w-full h-[420px] sm:h-[520px] object-cover transition-transform duration-[1.4s] group-hover:scale-105"
                                data-testid="craft-image-artisan"
                            />
                        </div>
                        <div className="absolute -bottom-8 -right-4 sm:-right-8 w-40 sm:w-56 overflow-hidden rounded-md border border-[#C58B49]/40 shadow-2xl group">
                            <img
                                src={IMAGES.tools}
                                alt="Leathercraft tools in the FnB workshop"
                                loading="lazy"
                                className="w-full h-32 sm:h-40 object-cover transition-transform duration-[1.4s] group-hover:scale-105"
                                data-testid="craft-image-tools"
                            />
                        </div>
                    </motion.div>
                    <div
                        className="absolute -top-10 -left-10 w-48 h-48 rounded-full blur-3xl pointer-events-none"
                        style={{ background: "radial-gradient(circle, rgba(197,139,73,0.18), transparent 70%)" }}
                    />
                </div>

                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                    >
                        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                            The Atelier
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.08]">
                            Tradition, engineered
                            <br />
                            for <em className="italic text-[#D4A373]">export</em>.
                        </h2>
                        <p className="mt-6 text-base text-[#C2B7AD] leading-relaxed">
                            FnB International combines traditional craftsmanship with modern
                            manufacturing. Our ability to understand each buyer's requirements —
                            and respond with flexible, made-to-order production — is our distinct
                            advantage, backed by modern machinery, skilled hands and a commitment
                            to quality, reliability and professionalism.
                        </p>
                    </motion.div>

                    <motion.blockquote
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                        className="mt-8 pl-5 border-l-2 border-[#C58B49]"
                        data-testid="founder-quote"
                    >
                        <p className="font-display italic text-xl sm:text-2xl text-[#E2D4C9] leading-snug">
                            "We set out to connect people and businesses across borders — building
                            lasting partnerships on trust, integrity and excellence."
                        </p>
                        <footer className="mt-3 text-xs tracking-[0.2em] uppercase text-[#8C8075]">
                            Mohd Farrukh — Founder, FnB International
                        </footer>
                    </motion.blockquote>

                    <div className="mt-10 grid sm:grid-cols-2 gap-6">
                        {PILLARS.map((p, i) => (
                            <motion.div
                                key={p.title}
                                data-testid={`pillar-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                                className="flex gap-4"
                            >
                                <div className="shrink-0 w-10 h-10 rounded-sm border border-[#2E2620] bg-[#1D1814] flex items-center justify-center text-[#C58B49]">
                                    <p.icon size={17} strokeWidth={1.6} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-medium text-[#F4EFEA] tracking-wide">{p.title}</h3>
                                    <p className="mt-1 text-[13px] text-[#8C8075] leading-relaxed">{p.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
